/**
 * PoolAppetitePage -- the RECEIVABLES_POOL_APPETITE policy domain on the
 * tenant policy-engine chassis (overdraft-coverage arc OC-3, decision D7).
 *
 * Route: /tempo/pool-appetite, flag-gated like the coverage page.
 *
 * Stage-3 stub posture: the CRUD surface is real (versions persist on the
 * chassis and are examiner-exportable), but nothing ENFORCES the appetite
 * until the funding-facilities arc wires OVERDRAFT_RECEIVABLES_POOL
 * participations through it. The page is therefore read-only-first: the
 * effective dials render as a definition list, and editing is an explicit
 * opt-in with the same immutable-publish affordance as the liquidity
 * policy.
 *
 * CHROME PLACEMENT (GH #2924). The level-1 heading and the subtitle render in
 * the loading, error and loaded states. The skeleton, the load-failure alert,
 * the no-version banner and the dials card are siblings below them, per KG
 * `technology-innovation/ui-package/2026-05-18-canonical-chrome-renders-outside-error-conditionals.md`.
 * The AppHeader chrome title reads "Pool Appetite Policy" on this route while
 * the read is pending, so a body carrying no heading during the skeleton
 * reports to a DOM-reading proof as a satisfied wait.
 *
 * READ OUTCOME (GH #2926, the GH #2913 class). Everything below the stub
 * warning renders from one outcome, QueryReadOutcome via
 * monitoring/read-state.ts:
 *
 *   loading -> skeleton
 *   failed  -> the load-failure alert alone; no banner, no dials, no edit
 *   unread  -> a not-yet-read notice alone. TanStack v5 reports a PAUSED
 *              first fetch (offline at mount) as pending with isFetching
 *              false, so isLoading is false with no data and no error.
 *   read    -> the no-version banner when the policy is null, then the dials
 *
 * Before this, the banner was gated on `!isLoading && !policy && !loadError`
 * and the dials on `!isLoading`. A paused first fetch therefore printed "No
 * pool-appetite policy has been published" over the baseline dials and an
 * "Edit dials" action, and a failed read rendered the dials beside the alert.
 * The 404-on-OFF path is unchanged: the hook maps that status to a null
 * policy, which is a read, so the banner states it.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  BodyText,
  Button,
  Card,
  Checkbox,
  Heading,
  Input,
  Skeleton,
  Badge,
} from '@aaimplatform/ui';
import {
  usePoolAppetitePolicy,
  usePublishPoolAppetite,
  type PoolAppetiteParameters,
  type PoolAppetitePolicyInput,
} from '../../hooks/useTempoPolicy';
import { deliveryModeBadgeStatus, deliveryModeLabel } from './delivery-mode';
import { BADGE_TONE } from '../collateral/CollateralListParts';
import { readOutcome, readTime, refreshFailed } from '../monitoring/read-state';

const SETTLEMENT_METHODS = ['WIRE', 'ACH', 'CRT'] as const;

const DEFAULT_FORM = {
  maxAggregateExposureUsd: '5000000',
  maxPerOriginatorRatio: '0.25',
  maxChargeOffRate12mo: '',
  maxChronicUseShare: '',
  minMedianCureDaysCeiling: '',
};

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDial(value: number | undefined, suffix = ''): string {
  return value === undefined ? 'Not set' : `${value}${suffix}`;
}

function parametersToForm(parameters: PoolAppetiteParameters): typeof DEFAULT_FORM {
  const asField = (value: number | undefined): string => (value === undefined ? '' : String(value));
  return {
    maxAggregateExposureUsd: asField(parameters.maxAggregateExposureUsd),
    maxPerOriginatorRatio: asField(parameters.maxPerOriginatorRatio),
    maxChargeOffRate12mo: asField(parameters.maxChargeOffRate12mo),
    maxChronicUseShare: asField(parameters.maxChronicUseShare),
    minMedianCureDaysCeiling: asField(parameters.minMedianCureDaysCeiling),
  };
}

export function PoolAppetitePage() {
  const policyQuery = usePoolAppetitePolicy();
  const { data: policy, error: loadError } = policyQuery;
  const outcome = readOutcome(policyQuery);
  const publishMutation = usePublishPoolAppetite();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [methods, setMethods] = useState<string[]>(['WIRE', 'ACH']);
  const [confirming, setConfirming] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);

  useEffect(() => {
    if (policy) {
      setForm(parametersToForm(policy.parameters));
      setMethods([...policy.parameters.allowedSettlementMethods]);
    }
  }, [policy]);

  const setField = (key: keyof typeof DEFAULT_FORM) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: event.target.value }));
      setConfirming(false);
      setPublishedVersion(null);
    };

  function toggleMethod(method: string, checked: boolean) {
    setMethods((prev) => (checked ? [...prev, method] : prev.filter((m) => m !== method)));
    setConfirming(false);
  }

  const requiredMissing = useMemo(
    () =>
      (['maxAggregateExposureUsd', 'maxPerOriginatorRatio'] as const).filter(
        (key) => form[key].trim() === '' || Number.isNaN(Number(form[key])),
      ),
    [form],
  );

  function buildInput(): PoolAppetitePolicyInput | null {
    if (requiredMissing.length > 0) {
      setLocalError(`Enter a numeric value for: ${requiredMissing.join(', ')}`);
      return null;
    }
    setLocalError(null);
    const optional = (raw: string): number | undefined =>
      raw.trim() === '' || Number.isNaN(Number(raw)) ? undefined : Number(raw);
    const parameters: PoolAppetiteParameters = {
      maxAggregateExposureUsd: Number(form.maxAggregateExposureUsd),
      maxPerOriginatorRatio: Number(form.maxPerOriginatorRatio),
      ...(optional(form.maxChargeOffRate12mo) !== undefined
        ? { maxChargeOffRate12mo: optional(form.maxChargeOffRate12mo) }
        : {}),
      ...(optional(form.maxChronicUseShare) !== undefined
        ? { maxChronicUseShare: optional(form.maxChronicUseShare) }
        : {}),
      ...(optional(form.minMedianCureDaysCeiling) !== undefined
        ? { minMedianCureDaysCeiling: optional(form.minMedianCureDaysCeiling) }
        : {}),
      allowedSettlementMethods:
        methods as PoolAppetiteParameters['allowedSettlementMethods'],
    };
    // GH #2744: the write shape carries no deliveryMode. The published
    // version's stamp is derived SERVER-side from Institution.tempoDeliveryMode
    // at publish time, matching MemberLiquidityPolicyInput on the same chassis.
    // Echoing the previous version's stamp back, as this did, re-asserted an
    // audit fact from stale client state on every republish.
    return { parameters };
  }

  function handleConfirmPublish() {
    const input = buildInput();
    if (!input) return;
    setConfirming(false);
    publishMutation.mutate(input, {
      onSuccess: (published) => {
        setPublishedVersion(published.policyVersion);
        setEditing(false);
      },
    });
  }

  const publishError = publishMutation.error;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          {/* Level 2: PortalLayout's AppHeader renders the page h1 (operator ruling on GH #2664, 2026-08-20). */}
          <Heading level={2} size="page">Pool Appetite Policy</Heading>
          <BodyText variant="ui" className="text-tertiary text-sm mt-1">
            Investor-side appetite for overdraft receivables pool participations -- concentration,
            quality floors, and settlement methods.
          </BodyText>
        </div>
        {policy && (
          <div className="flex items-center gap-3 shrink-0">
            {/* Routed through the shared helpers so a pre-migration version with
                no stamp reads "Not recorded" rather than indexing to undefined. */}
            <Badge variant={BADGE_TONE[deliveryModeBadgeStatus(policy.deliveryMode)]} size="small">
              {deliveryModeLabel(policy.deliveryMode)}
            </Badge>
            <div className="text-right">
              <BodyText variant="ui" className="text-sm text-primary font-mono">
                Version {policy.policyVersion}
              </BodyText>
              <BodyText variant="ui" className="text-xs text-tertiary">
                Published {formatDateTime(policy.updatedAt)}
              </BodyText>
            </div>
          </div>
        )}
      </div>

      <Alert type="warning" title="Stage-3 stub">
        The appetite dials persist as versioned policy now, but nothing enforces them yet --
        enforcement against OVERDRAFT_RECEIVABLES_POOL participations lands with the
        funding-facilities arc.
      </Alert>

      {outcome === 'loading' && (
        <div className="space-y-4" data-testid="pool-appetite-loading">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {outcome === 'failed' && (
        <Alert type="error" title="Policy load failed">
          <div className="space-y-1">
            <div>{loadError?.message}</div>
            <div>No version was read, so this page shows no dials and no edit action.</div>
          </div>
        </Alert>
      )}

      {outcome === 'unread' && (
        <div className="p-4 rounded-sm bg-accent/5 border border-recessed" role="status">
          <BodyText variant="ui" className="text-sm text-secondary">
            The pool-appetite policy has not been read yet. This page shows no version, no dials and
            no edit action until the read completes.
          </BodyText>
        </div>
      )}

      {/* A background refetch rejected over a successful read. The version and
          dials below are that earlier read, so they stay and the notice says
          when it was taken. */}
      {refreshFailed(policyQuery) && (
        <Alert type="warning" title="Policy refresh failed">
          {`The version and dials shown are from the read at ${readTime(policyQuery.dataUpdatedAt)}.${loadError ? ` The latest refresh failed: ${loadError.message}` : ''}`}
        </Alert>
      )}

      {/* `read` with a null policy is the measured absence: the hook maps the
          404 to null. Every other outcome says nothing about what this
          institution has published, and the dials below would read as the
          appetite in force. */}
      {outcome === 'read' && !policy && (
        <div className="p-4 rounded-sm bg-accent/5 border border-recessed">
          <BodyText variant="ui" className="text-sm text-secondary">
            No pool-appetite policy has been published for your institution yet. The values below
            are baseline dials; publishing creates version 1.
          </BodyText>
        </div>
      )}

      {outcome === 'read' && !editing && (
        <Card className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <Heading level={2} size="card" className="font-sans">Effective Dials</Heading>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Edit dials
            </Button>
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <div className="flex justify-between gap-4 border-b border-recessed pb-2">
              <dt className="text-sm font-sans text-tertiary">Max aggregate exposure (USD)</dt>
              <dd className="text-sm font-mono text-primary tabular-nums">
                {policy ? formatDial(policy.parameters.maxAggregateExposureUsd) : formatDial(Number(DEFAULT_FORM.maxAggregateExposureUsd))}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-recessed pb-2">
              <dt className="text-sm font-sans text-tertiary">Max per-originator share</dt>
              <dd className="text-sm font-mono text-primary tabular-nums">
                {policy ? formatDial(policy.parameters.maxPerOriginatorRatio) : formatDial(Number(DEFAULT_FORM.maxPerOriginatorRatio))}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-recessed pb-2">
              <dt className="text-sm font-sans text-tertiary">Max 12-month charge-off rate</dt>
              <dd className="text-sm font-mono text-primary tabular-nums">
                {formatDial(policy?.parameters.maxChargeOffRate12mo)}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-recessed pb-2">
              <dt className="text-sm font-sans text-tertiary">Max chronic-use share</dt>
              <dd className="text-sm font-mono text-primary tabular-nums">
                {formatDial(policy?.parameters.maxChronicUseShare)}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-recessed pb-2">
              <dt className="text-sm font-sans text-tertiary">Median cure-days ceiling</dt>
              <dd className="text-sm font-mono text-primary tabular-nums">
                {formatDial(policy?.parameters.minMedianCureDaysCeiling, ' days')}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-recessed pb-2">
              <dt className="text-sm font-sans text-tertiary">Allowed settlement methods</dt>
              <dd className="text-sm font-mono text-primary">
                {(policy ? policy.parameters.allowedSettlementMethods : methods).join(', ') || 'None'}
              </dd>
            </div>
          </dl>
        </Card>
      )}

      {outcome === 'read' && editing && (
        <Card className="p-6">
          <Heading level={2} size="card" className="font-sans mb-4">Edit Dials</Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Max aggregate exposure (USD)"
              type="number"
              min={0}
              step={100000}
              value={form.maxAggregateExposureUsd}
              onChange={setField('maxAggregateExposureUsd')}
              helperText="Cap on total pool exposure across originators"
            />
            <Input
              label="Max per-originator share"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={form.maxPerOriginatorRatio}
              onChange={setField('maxPerOriginatorRatio')}
              helperText="Share of aggregate exposure from one originating FI (greater than 0, at most 1)"
            />
            <Input
              label="Max 12-month charge-off rate"
              type="number"
              min={0}
              max={1}
              step={0.005}
              value={form.maxChargeOffRate12mo}
              onChange={setField('maxChargeOffRate12mo')}
              helperText="Quality floor; blank leaves the floor unset"
            />
            <Input
              label="Max chronic-use share"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={form.maxChronicUseShare}
              onChange={setField('maxChronicUseShare')}
              helperText="Quality floor; blank leaves the floor unset"
            />
            <Input
              label="Median cure-days ceiling"
              type="number"
              min={0}
              step={1}
              value={form.minMedianCureDaysCeiling}
              onChange={setField('minMedianCureDaysCeiling')}
              helperText="Reject pools whose median cure time exceeds this many days; blank leaves it unset"
            />
            <div>
              <BodyText variant="ui" className="text-sm text-secondary mb-2">
                Allowed settlement methods
              </BodyText>
              <div className="space-y-2">
                {SETTLEMENT_METHODS.map((method) => (
                  <Checkbox
                    key={method}
                    label={method === 'CRT' ? 'CRT (opt-in settlement)' : method}
                    checked={methods.includes(method)}
                    onChange={(checked) => toggleMethod(method, checked)}
                  />
                ))}
              </div>
            </div>
          </div>

          {localError && (
            <Alert type="warning" title="Check the form" className="mt-4">
              {localError}
            </Alert>
          )}

          {publishError && (
            <Alert type="error" title="Publish rejected" className="mt-4">
              <div className="space-y-1">
                <div>{publishError.message}</div>
                {publishError.details && publishError.details.length > 0 && (
                  <ul className="list-disc pl-5">
                    {publishError.details.map((detail, index) => (
                      <li key={index}>
                        {detail.issue ?? detail.message ?? 'Validation failure'}
                        {detail.path ? ` (${detail.path})` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Alert>
          )}

          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-recessed">
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(false);
                setConfirming(false);
                setLocalError(null);
                if (policy) {
                  setForm(parametersToForm(policy.parameters));
                  setMethods([...policy.parameters.allowedSettlementMethods]);
                }
              }}
            >
              Cancel
            </Button>
            {confirming ? (
              <>
                <BodyText variant="ui" className="text-sm text-secondary">
                  Publish immutable version {(policy?.policyVersion ?? 0) + 1}?
                </BodyText>
                <Button
                  variant="primary"
                  onClick={handleConfirmPublish}
                  disabled={publishMutation.isPending}
                >
                  {publishMutation.isPending ? 'Publishing...' : 'Confirm publish'}
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  if (buildInput()) setConfirming(true);
                }}
                disabled={publishMutation.isPending}
              >
                Publish new version
              </Button>
            )}
          </div>
        </Card>
      )}

      {publishedVersion !== null && (
        <Alert type="success" title="Version published">
          Version {publishedVersion} is now the effective pool-appetite policy.
        </Alert>
      )}
    </div>
  );
}
